import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Trash2, Loader2, Mail } from 'lucide-react';

interface DocumentActionModalProps {
    isOpen: boolean;
    type: 'success' | 'error' | 'delete' | 'confirm';
    title: string;
    message: string;
    onClose: () => void;
    onConfirm?: () => void; // For delete or confirm
    onNotify?: () => void; // For email notification
    isLoading?: boolean;
    showNotifyBtn?: boolean;
}

const DocumentActionModal: React.FC<DocumentActionModalProps> = ({
    isOpen,
    type,
    title,
    message,
    onClose,
    onConfirm,
    onNotify,
    isLoading = false,
    showNotifyBtn = false
}) => {
    const { t } = useTranslation('documents');

    if (!isOpen) return null;

    const isSuccess = type === 'success';
    const isError = type === 'error';
    const isDelete = type === 'delete';
    const isConfirm = type === 'confirm';

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden transform transition-all scale-100">
                <div className={`p-6 flex flex-col items-center text-center ${isDelete ? 'bg-red-50' : isConfirm ? 'bg-blue-50' : 'bg-white'}`}>
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-lg ${isSuccess ? 'bg-green-100 text-green-600' : isError ? 'bg-red-100 text-red-600' : isConfirm ? 'bg-blue-100 text-blue-600' : 'bg-white text-red-500'}`}>
                        {isSuccess && <CheckCircle size={32} strokeWidth={3} />}
                        {isError && <XCircle size={32} strokeWidth={3} />}
                        {isDelete && <Trash2 size={32} strokeWidth={2} />}
                        {isConfirm && <Mail size={32} strokeWidth={2} />}
                    </div>

                    <h3 className={`text-xl font-bold mb-2 ${isDelete ? 'text-red-600' : 'text-slate-900'}`}>
                        {title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed">
                        {message}
                    </p>
                </div>

                <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-3 justify-center">
                    {isDelete ? (
                        <>
                            <button
                                onClick={onClose}
                                className="px-5 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-200 transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={onConfirm}
                                disabled={isLoading}
                                className="px-5 py-2.5 rounded-xl font-bold bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-red-500/30 transition-all flex items-center gap-2"
                            >
                                {isLoading && <Loader2 className="animate-spin" size={16} />}
                                {isLoading ? t('common.deleting') : t('actions.delete')}
                            </button>
                        </>
                    ) : type === 'confirm' ? (
                        <>
                            <button
                                onClick={onClose}
                                className="px-5 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-200 transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={onConfirm}
                                disabled={isLoading}
                                className="px-5 py-2.5 rounded-xl font-bold bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-blue-500/30 transition-all flex items-center gap-2"
                            >
                                {isLoading && <Loader2 className="animate-spin" size={16} />}
                                {isLoading ? t('common.sending') : t('actions.confirm')}
                            </button>
                        </>
                    ) : (
                        <div className="flex gap-2 w-full">
                            <button
                                onClick={onClose}
                                className={`flex-1 px-5 py-2.5 rounded-xl font-bold text-slate-700 hover:bg-slate-200 transition-colors border border-slate-200 bg-white`}
                            >
                                {t('actions.close')}
                            </button>

                            {isSuccess && showNotifyBtn && onNotify && (
                                <button
                                    onClick={onNotify}
                                    disabled={isLoading}
                                    className="flex-1 px-5 py-2.5 rounded-xl font-bold bg-black text-white hover:bg-slate-800 shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                                >
                                    {isLoading ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />}
                                    {isLoading ? t('common.sending') : t('actions.notify_client')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};

export default DocumentActionModal;
